import { useRef, useState } from 'react';
import { parseGradient, formatGradient } from '../../utils/gradientCss';
import { parseCssColor, formatCssColor } from '../../utils/cssColor';
import IconRemove from '../../components/icons/IconRemove';
import language from '../../variables/language';

// Structured editor for the app's 4 gradient tokens: type (linear/radial),
// angle or shape, and an arbitrary number of color stops - edited directly
// on an interactive bar (drag handles to reposition, click empty space to
// add a stop) rather than a stack of numeric-input rows, closer to how
// Figma/Photoshop gradient editors work. Falls back to a plain text input
// when the value doesn't match either shape gradientCss.js actually parses
// - editing stays possible, just without the structured controls, rather
// than silently discarding an unusual value.
export default function GradientEditor({ value, onChange }) {
  const t = language[language.lang].themeSettings;
  const parsed = parseGradient(value);
  const [rawFallback, setRawFallback] = useState(value);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const barRef = useRef(null);
  const dragIndexRef = useRef(null);
  const didDragRef = useRef(false);

  if (!parsed) {
    return (
      <div className="gradient-editor gradient-editor-fallback">
        <div className="gradient-editor-preview" style={{ background: value }} />
        <input
          type="text"
          className="theme-editor-text-input"
          value={rawFallback}
          onChange={(e) => { setRawFallback(e.target.value); onChange(e.target.value); }}
        />
      </div>
    );
  }

  const update = (patch) => onChange(formatGradient({ ...parsed, ...patch }));

  const updateStopAt = (index, patch, stopsBase) => {
    const stops = (stopsBase || parsed.stops).map((s, i) => (i === index ? { ...s, ...patch } : s));
    update({ stops });
  };

  const positionFromClientX = (clientX) => {
    const rect = barRef.current.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    return Math.max(0, Math.min(100, Math.round(ratio * 100)));
  };

  const nearestStopColor = (position) => {
    let closest = parsed.stops[0];
    let closestDist = Infinity;
    for (const s of parsed.stops) {
      const dist = Math.abs((s.position ?? 0) - position);
      if (dist < closestDist) { closest = s; closestDist = dist; }
    }
    return closest ? closest.color : '#ffffff';
  };

  const handleBarClick = (e) => {
    if (didDragRef.current) { didDragRef.current = false; return; }
    const position = positionFromClientX(e.clientX);
    const stops = [...parsed.stops, { color: nearestStopColor(position), position }]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const newIndex = stops.findIndex((s) => s.position === position);
    update({ stops });
    setSelectedIndex(newIndex);
  };

  const handleHandlePointerDown = (index, e) => {
    e.stopPropagation();
    e.preventDefault();
    setSelectedIndex(index);
    dragIndexRef.current = index;
    didDragRef.current = false;

    const onMove = (moveEvent) => {
      didDragRef.current = true;
      const position = positionFromClientX(moveEvent.clientX);
      updateStopAt(dragIndexRef.current, { position });
    };
    const onUp = () => {
      dragIndexRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const removeStop = (index) => {
    if (parsed.stops.length <= 2) return;
    update({ stops: parsed.stops.filter((_, i) => i !== index) });
    setSelectedIndex(Math.max(0, index - 1));
  };

  const activeIndex = Math.min(selectedIndex, parsed.stops.length - 1);
  const activeStop = parsed.stops[activeIndex];
  const { hex, alpha } = parseCssColor(activeStop.color);

  return (
    <div className="gradient-editor">
      <div className="gradient-editor-controls">
        <div className="gradient-editor-type-row">
          <div className="gradient-editor-type-toggle">
            <span
              className={parsed.type === 'linear' ? 'active' : ''}
              onClick={() => parsed.type !== 'linear' && update({ type: 'linear', angle: 90 })}
            >
              {t.gradientLinear}
            </span>
            <span
              className={parsed.type === 'radial' ? 'active' : ''}
              onClick={() => parsed.type !== 'radial' && update({ type: 'radial', shape: 'circle' })}
            >
              {t.gradientRadial}
            </span>
          </div>
          {parsed.type === 'linear' ? (
            <label className="gradient-editor-angle">
              <input
                type="range"
                min="0"
                max="360"
                value={parsed.angle}
                onChange={(e) => update({ angle: Number(e.target.value) })}
              />
              <span>{parsed.angle}°</span>
            </label>
          ) : (
            <select
              className="gradient-editor-shape-select"
              value={parsed.shape}
              onChange={(e) => update({ shape: e.target.value })}
            >
              <option value="circle">{t.gradientCircle}</option>
              <option value="ellipse">{t.gradientEllipse}</option>
            </select>
          )}
        </div>

        <div className="gradient-editor-bar-checker">
          <div
            className="gradient-editor-bar"
            ref={barRef}
            style={{ background: value }}
            onClick={handleBarClick}
          >
            {parsed.stops.map((stop, index) => (
              <span
                key={index}
                className={'gradient-editor-handle' + (index === activeIndex ? ' active' : '')}
                style={{ left: (stop.position ?? 0) + '%', background: stop.color }}
                onPointerDown={(e) => handleHandlePointerDown(index, e)}
              />
            ))}
          </div>
        </div>
        <p className="gradient-editor-hint">{t.gradientBarHint}</p>

        <div className="gradient-editor-active-stop">
          <input type="color" value={hex} onChange={(e) => updateStopAt(activeIndex, { color: formatCssColor(e.target.value, alpha) })} />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={alpha}
            onChange={(e) => updateStopAt(activeIndex, { color: formatCssColor(hex, Number(e.target.value)) })}
          />
          <input
            type="number"
            min="0"
            max="100"
            className="gradient-editor-position"
            value={activeStop.position != null ? activeStop.position : ''}
            onChange={(e) => updateStopAt(activeIndex, { position: e.target.value === '' ? null : Number(e.target.value) })}
          />
          <span className="gradient-editor-percent">%</span>
          <span
            className={'gradient-editor-remove-stop' + (parsed.stops.length <= 2 ? ' disabled' : '')}
            onClick={() => removeStop(activeIndex)}
          >
            <IconRemove />
          </span>
        </div>
      </div>
    </div>
  );
}
